
import { formatDistanceToNow } from "date-fns";
import { UserRole } from "@/lib/types";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { UserRoleBadge } from "./UserRoleBadge";
import { RefreshCw, Mail, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState } from "react";

export interface InvitedUser {
  id: string;
  email: string;
  name: string | null;
  company_name: string | null;
  roles: UserRole[];
  status: "pending" | "accepted" | "expired";
  created_at: string;
  expires_at: string;
}

interface InvitedUsersTableProps {
  invitedUsers: InvitedUser[];
  isLoading: boolean;
  onResendInvitation: (userId: string) => Promise<void>;
  onDeleteInvitation: (userId: string) => Promise<void>;
}

export function InvitedUsersTable({ 
  invitedUsers, 
  isLoading,
  onResendInvitation,
  onDeleteInvitation
}: InvitedUsersTableProps) {
  const [activeTab, setActiveTab] = useState<"pending" | "accepted" | "expired">("pending");
  
  const pendingInvitations = invitedUsers.filter(user => user.status === "pending");
  const acceptedInvitations = invitedUsers.filter(user => user.status === "accepted");
  const expiredInvitations = invitedUsers.filter(user => 
    user.status === "expired" || (user.status === "pending" && new Date(user.expires_at) < new Date())
  );
  
  return (
    <div>
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "pending" | "accepted" | "expired")}>
        <TabsList className="mb-4">
          <TabsTrigger value="pending">
            Pending ({pendingInvitations.length})
          </TabsTrigger>
          <TabsTrigger value="accepted">
            Accepted ({acceptedInvitations.length})
          </TabsTrigger>
          <TabsTrigger value="expired">
            Expired ({expiredInvitations.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="pending" className="rounded-md border">
          <InvitationTable 
            invitations={pendingInvitations} 
            isLoading={isLoading} 
            emptyMessage="No pending invitations"
            onResendInvitation={onResendInvitation}
            onDeleteInvitation={onDeleteInvitation}
            showActions={true}
          />
        </TabsContent>
        
        <TabsContent value="accepted" className="rounded-md border">
          <InvitationTable 
            invitations={acceptedInvitations} 
            isLoading={isLoading} 
            emptyMessage="No accepted invitations"
            onResendInvitation={onResendInvitation}
            onDeleteInvitation={onDeleteInvitation}
            showActions={false}
          />
        </TabsContent>
        
        <TabsContent value="expired" className="rounded-md border">
          <InvitationTable 
            invitations={expiredInvitations} 
            isLoading={isLoading} 
            emptyMessage="No expired invitations"
            onResendInvitation={onResendInvitation}
            onDeleteInvitation={onDeleteInvitation}
            showActions={true}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

interface InvitationTableProps {
  invitations: InvitedUser[];
  isLoading: boolean;
  emptyMessage: string;
  onResendInvitation: (userId: string) => Promise<void>;
  onDeleteInvitation: (userId: string) => Promise<void>;
  showActions: boolean;
}

function InvitationTable({
  invitations,
  isLoading,
  emptyMessage,
  onResendInvitation,
  onDeleteInvitation,
  showActions
}: InvitationTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Email</TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Roles</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Invited</TableHead>
          <TableHead>Expires</TableHead>
          {showActions && <TableHead className="text-right">Actions</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {invitations.length === 0 ? (
          <TableRow>
            <TableCell colSpan={showActions ? 7 : 6} className="text-center h-24">
              {isLoading ? "Loading invited users..." : emptyMessage}
            </TableCell>
          </TableRow>
        ) : (
          invitations.map(user => (
            <TableRow key={user.id}>
              <TableCell>{user.email}</TableCell>
              <TableCell>{user.name || "N/A"}</TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {user.roles?.map(role => (
                    <UserRoleBadge 
                      key={role} 
                      role={role}
                    />
                  ))}
                </div>
              </TableCell>
              <TableCell>
                <Badge 
                  variant={user.status === "pending" ? "outline" : user.status === "accepted" ? "default" : "destructive"}
                  className={user.status === "pending" ? "text-amber-500 border-amber-500" : ""}
                >
                  {user.status === "pending" ? "Pending" : 
                   user.status === "accepted" ? "Accepted" : "Expired"}
                </Badge>
              </TableCell>
              <TableCell>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>{formatDistanceToNow(new Date(user.created_at), { addSuffix: true })}</span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {new Date(user.created_at).toLocaleDateString()} {new Date(user.created_at).toLocaleTimeString()}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableCell>
              <TableCell>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>{formatDistanceToNow(new Date(user.expires_at), { addSuffix: true })}</span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {new Date(user.expires_at).toLocaleDateString()} {new Date(user.expires_at).toLocaleTimeString()}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableCell>
              {showActions && (
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {user.status === "pending" && (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => onResendInvitation(user.id)}
                        >
                          <Mail className="h-4 w-4 mr-1" />
                          Resend
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => onDeleteInvitation(user.id)}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
